/**
 * Sindoori's four vocabulary bands — LETTER-LAGOON-PLAN.md §3.2, from her NCF
 * alignment documents.
 *
 * 🔴 CONTENT AUTHORS DRAW FROM THESE AND NOWHERE ELSE. *"Do not invent cue
 * words."* The pools were chosen by the person hired to choose them, against
 * NCF-FS, for Indian home / school / neighbourhood contexts — a word added by
 * whoever happened to be authoring that afternoon is unreviewed content in a
 * preschool product, and nothing downstream would ever flag it.
 *
 * 🔴 THEY ARE DATA SO THAT THE RULE IS A TEST. As prose in a plan this was a
 * thing to remember; here it is `isPoolWord`, and `content.test.ts` fails the
 * build on a cue word nobody approved.
 *
 * 🔴 RECYCLING IS THE MECHANISM, NOT A SHORTFALL — her strongest content note:
 * *"Vocabulary should be recycled across multiple contexts rather than
 * introduced only once."* The same noun is an L1 cue, an L2 rhyme partner and
 * an L3 beginning-sound target. Meeting `mango` three times is the design.
 *
 * ⚠️ THE BANDS OVERLAP BY DESIGN, and there are four of them against three
 * levels — which is why `Item.bandHint` exists separately from `Item.band`. Her
 * own caveat is why the overlap is not a defect: *"The band wise progressions
 * should be treated as developmental ranges intended to guide content design
 * rather than as fixed expectations that all children will meet at a particular
 * age."*
 */

/**
 * Her four bands, named as she names them.
 *
 * 🔴 DELIBERATELY NOT THE REPO'S `Band` TYPE, AND THEY MUST NOT BE MERGED.
 * `Band` is `BAND_3_4 | BAND_4_5 | BAND_5_6` — three ranges, shared by five
 * games, documented as *"authored everywhere, read nowhere"*. Hers are four with
 * a deliberate overlap (B3 is 4.5–5.5, straddling two of the repo's), so no
 * mapping exists that does not lose information. Widening `Band` would change
 * the content model of four other games to describe one game's vocabulary bank.
 */
export type CurriculumBand = 'B1' | 'B2' | 'B3' | 'B4'

/** Ages each band was written for. Adult-facing; nothing selects on it yet. */
export const BAND_AGES: Readonly<Record<CurriculumBand, string>> = {
  B1: '3–4',
  B2: '4–5',
  B3: '4.5–5.5',
  B4: '5.5–6',
}

/** B1 · ages 3–4 — 25 nouns. */
export const POOL_B1: readonly string[] = [
  'ball', 'cup', 'book', 'bag', 'shoe', 'hat', 'spoon', 'plate', 'banana', 'mango',
  'apple', 'flower', 'tree', 'leaf', 'fish', 'frog', 'cow', 'dog', 'cat', 'bus',
  'car', 'kite', 'water', 'moon', 'star',
]

/** B2 · ages 4–5 — 24 nouns. */
export const POOL_B2: readonly string[] = [
  'pencil', 'eraser', 'bottle', 'basket', 'coconut', 'papaya', 'auto', 'train', 'river',
  'pond', 'nest', 'feather', 'butterfly', 'duck', 'goat', 'hen', 'drum', 'ladder',
  'umbrella', 'soap', 'towel', 'garden', 'seed', 'pot',
]

/** B3 · ages 4.5–5.5 — 25 nouns. */
export const POOL_B3: readonly string[] = [
  'farmer', 'doctor', 'teacher', 'market', 'temple', 'bridge', 'forest', 'village',
  'basket', 'peacock', 'tiger', 'squirrel', 'turtle', 'tadpole', 'rainbow', 'mountain',
  'bucket', 'necklace', 'blanket', 'festival', 'garden', 'parrot', 'riverbank',
  'beehive', 'pathway',
]

/** B4 · ages 5.5–6 — 23 nouns. */
export const POOL_B4: readonly string[] = [
  'carpenter', 'musician', 'painter', 'journey', 'direction', 'pattern', 'clock',
  'meadow', 'footprint', 'community', 'celebration', 'instrument', 'orchestra',
  'village fair', 'seeding', 'workshop', 'sculpture', 'backpack', 'adventure',
  'message', 'puzzle', 'toolbox', 'flower bouquet',
]

export const POOLS: Readonly<Record<CurriculumBand, readonly string[]>> = {
  B1: POOL_B1,
  B2: POOL_B2,
  B3: POOL_B3,
  B4: POOL_B4,
}

const ALL_BANDS = ['B1', 'B2', 'B3', 'B4'] as const

/** Is this a word a content author is allowed to use as a cue? */
export function isPoolWord(word: string): boolean {
  return bandOf(word) !== null
}

/**
 * The earliest band a word appears in, or null.
 *
 * Earliest rather than all, because two words appear twice — `basket` in B2 and
 * B3, `garden` in B2 and B3 — and the earliest is the one that bounds who may
 * meet it.
 */
export function bandOf(word: string): CurriculumBand | null {
  const w = word.toLowerCase()
  for (const band of ALL_BANDS) {
    if (POOLS[band].includes(w)) return band
  }
  return null
}

/** Every pool word starting with `letter`, earliest band first. */
export function poolWordsStartingWith(letter: string): string[] {
  const l = letter.toLowerCase()
  return ALL_BANDS.flatMap((b) => POOLS[b].filter((w) => w.startsWith(l)))
}

/* ─────────────────────────────────────────── rhyme and minimal pairs ── */

/**
 * B3's rhyme pairs.
 *
 * How they are used matters as much as the list: *"the focus must remain
 * playful and auditory rather than explicitly instructional."*
 */
export const RHYME_PAIRS: readonly (readonly [string, string])[] = [
  ['cat', 'hat'],
  ['sun', 'fun'],
  ['bell', 'well'],
  ['log', 'frog'],
  ['pin', 'tin'],
]

/**
 * B4's minimal pairs — the **eight this game can use**.
 *
 * 🔴 SHE LISTS TEN. Two are excluded below, by name, with the reason.
 */
export const MINIMAL_PAIRS: readonly (readonly [string, string])[] = [
  ['pin', 'bin'],
  ['ten', 'den'],
  ['coat', 'goat'],
  ['fan', 'van'],
  ['cap', 'gap'],
  ['bat', 'pat'],
  ['sip', 'zip'],
  ['tuck', 'duck'],
]

/**
 * 🔴 THE TWO MINIMAL PAIRS THIS GAME CANNOT USE, EXCLUDED BY NAME.
 *
 * `rope`/`road` and `rice`/`rise` contrast on the **final** phoneme. Both
 * members of each pair *start with the same sound*, so a stone game asking
 * *"which one starts with /r/?"* has no answer — both stones are right, and a
 * child choosing either is told they are wrong half the time.
 *
 * ⚠️ THEY ARE VALID MATERIAL, JUST NOT FOR THIS MECHANIC. They are good
 * auditory-discrimination items and belong in a listening task.
 *
 * 🔴 NAMED RATHER THAN OMITTED, WHICH IS THE ENTIRE POINT OF THIS CONSTANT. A
 * bare omission is what a well-meaning edit restores six months from now — "she
 * listed ten and we only have eight" is a bug report waiting to be filed.
 * `content.test.ts` asserts their absence against this list.
 */
export const EXCLUDED_MINIMAL_PAIRS: readonly {
  pair: readonly [string, string]
  reason: string
}[] = [
  {
    pair: ['rope', 'road'],
    reason: 'final-phoneme contrast — both start with /r/, so a beginning-sound item has no answer',
  },
  {
    pair: ['rice', 'rise'],
    reason: 'final-phoneme contrast — both start with /r/, so a beginning-sound item has no answer',
  },
]

/** Every word this game may never use in a beginning-sound item. */
export const EXCLUDED_WORDS: readonly string[] = EXCLUDED_MINIMAL_PAIRS.flatMap((e) => e.pair)
